import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatPercent, formatRoas, formatNumber } from "@/lib/formatters";

type MetricKey = "roas" | "spend" | "ctr" | "purchases";
const TOGGLES: { key: MetricKey; label: string; fmt: (v: number) => string }[] = [
  { key: "roas", label: "ROAS", fmt: formatRoas },
  { key: "spend", label: "Spend", fmt: formatCurrency },
  { key: "ctr", label: "CTR", fmt: formatPercent },
  { key: "purchases", label: "Purchases", fmt: formatNumber },
];

const AGE_RANGES = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];
const GENDERS = ["male", "female"];

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function AgeGenderHeatmap({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const [metric, setMetric] = useState<MetricKey>("roas");
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-breakdown-age-gender", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("breakdown/age-gender", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const lookup = new Map<string, Record<string, number>>();
  (data || []).forEach((d: Record<string, string | number>) => {
    const key = `${d.ageRange}_${d.gender}`;
    lookup.set(key, d as Record<string, number>);
  });

  const allValues = (data || []).map((d: Record<string, number>) => d[metric] ?? 0);
  const maxVal = Math.max(...allValues, 1);
  const minVal = Math.min(...allValues, 0);

  const getIntensity = (v: number) => {
    const norm = maxVal > minVal ? (v - minVal) / (maxVal - minVal) : 0;
    const alpha = 0.1 + norm * 0.6;
    return `rgba(99, 102, 241, ${alpha})`;
  };

  const fmtFn = TOGGLES.find(t => t.key === metric)!.fmt;
  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Age & Gender Heatmap" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#8C8578" }} data-testid="empty-age-gender">No demographic data available</div>
      ) : (
        <div className="px-4 pb-3 h-full flex flex-col" data-testid="chart-age-gender">
          <div className="flex gap-1 mb-3">
            {TOGGLES.map(t => (
              <button
                key={t.key}
                onClick={() => setMetric(t.key)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${
                  metric === t.key ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 text-gray-400 hover:text-gray-600"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="text-left text-gray-400 font-medium pb-2 pr-2">Age</th>
                  {GENDERS.map(g => (
                    <th key={g} className="text-center text-gray-400 font-medium pb-2 capitalize">{g}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {AGE_RANGES.map(age => (
                  <tr key={age}>
                    <td className="text-gray-600 font-medium py-1 pr-2">{age}</td>
                    {GENDERS.map(gender => {
                      const row = lookup.get(`${age}_${gender}`);
                      const v = row?.[metric] ?? 0;
                      return (
                        <td key={gender} className="py-1 px-1">
                          <div
                            className="rounded-md text-center py-2 text-xs font-medium text-gray-700 cursor-default group/cell relative"
                            style={{ backgroundColor: getIntensity(v) }}
                          >
                          <div className="hidden group-hover/cell:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-2.5 whitespace-nowrap text-left">
                            <p className="text-[11px] font-semibold text-gray-900 mb-1 capitalize">{age} · {gender}</p>
                            <div className="space-y-0.5 text-[10px] text-gray-600">
                              <p>Spend: <span className="font-medium text-gray-900">{formatCurrency(row?.spend ?? 0)}</span></p>
                              <p>Purchases: <span className="font-medium text-gray-900">{formatNumber(row?.purchases ?? 0)}</span></p>
                              <p>CTR: <span className="font-medium text-gray-900">{formatPercent(row?.ctr ?? 0)}</span></p>
                              <p>ROAS: <span className="font-medium text-gray-900">{formatRoas(row?.roas ?? 0)}</span></p>
                            </div>
                          </div>
                            {fmtFn(v)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
